import {
  Link,
  Form,
  InputText,
  useFormConfig,
  Button,
  FormAction,
  FormField,
  FormGrid,
  FormRow,
  FormTitle,
  displayErrorToast,
} from "@endya-conformite/ui-kit";
import Loader from "@endya-conformite/ui-kit/src/assets/loader.svg?react";
import { zod } from "@endya-conformite/ui-kit/src/lya/exports";
import { GatewayAuthApi } from "@src/data/auth/auth.api";
import { handleGatewayError } from "@src/data/utils/handleGatewayError";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useState } from "react";
import { ForgotPasswordOutletContext } from "./ForgotPassword";
import style from "./ForgotPasswordMail.module.scss";

const schema = zod.object({
  email: zod.string().nonempty().email(),
});

export type ForgotPasswordMailFormValue = zod.infer<typeof schema>;

export function ForgotPasswordMail() {
  const navigate = useNavigate();
  const { data, setData } = useOutletContext<ForgotPasswordOutletContext>();
  const formConfig = useFormConfig<typeof schema, ForgotPasswordMailFormValue>({
    schema,
    defaultValues: {
      email: data.email,
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (submittedData: ForgotPasswordMailFormValue) => {
    setIsSubmitting(true);
    try {
      await GatewayAuthApi.forgotPassword(submittedData.email);
      setData(submittedData);
      navigate("/forgot-password/reset", {
        state: {
          email: submittedData.email,
        },
      });
    } catch (err) {
      handleGatewayError({
        onResponse: (error) => {
          displayErrorToast(
            error.response.data.message ||
              "Une erreur est survenue lors de l'envoi du code. Veuillez réessayer."
          );
        },
        onUnhandled: () => {
          displayErrorToast(
            "Impossible de contacter le serveur. Veuillez vérifier votre connexion internet et réessayer."
          );
        },
      })(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form formConfig={formConfig} onSubmit={handleSubmit}>
      <FormGrid disableHorizontalGutter>
        <FormTitle>
          <h1>Mot de passe oublié ?</h1>
        </FormTitle>
        <FormRow>
          <FormField>
            <p className={style.description}>
              Pour réinitialiser votre mot de passe, veuillez saisir votre
              adresse e-mail. Vous recevrez un code provisoire pour
              réinitialiser votre mot de passe.
            </p>
          </FormField>
        </FormRow>
        <FormRow>
          <FormField>
            <InputText<ForgotPasswordMailFormValue>
              name="email"
              label="Adresse email"
            />
          </FormField>
        </FormRow>
        <FormAction className={style.buttons}>
          <Link className={style.link} to="/login">
            Annuler
          </Link>
          <Button
            type="submit"
            disabled={isSubmitting}
            icon={isSubmitting ? <Loader /> : undefined}
          >
            Envoyer
          </Button>
        </FormAction>
      </FormGrid>
    </Form>
  );
}
